import * as SecureStore from 'expo-secure-store';
import * as React from 'react';
import { Platform } from 'react-native';

const RECENT_SEARCHES_KEY = 'radious_recent_searches';
const RECENT_SEARCH_LIMIT = 15;

export type RecentSearchEntry = {
  key: string;
  type: 'deal' | 'event' | 'profile' | 'venue' | 'query';
  title: string;
  subtitle?: string;
  image_url?: string | null;
  poster_image?: {
    public_url: string;
    position?: number;
    focal_point_x?: number;
    focal_point_y?: number;
    focal_scale?: number;
  } | null;
  route?: `/events/${string}` | `/profiles/${string}` | `/venues/${string}`;
  query?: string;
};

function isRecentSearchEntry(value: unknown): value is RecentSearchEntry {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const entry = value as Record<string, unknown>;
  return (
    typeof entry.key === 'string' &&
    (entry.type === 'event' ||
      entry.type === 'deal' ||
      entry.type === 'profile' ||
      entry.type === 'venue' ||
      entry.type === 'query') &&
    typeof entry.title === 'string' &&
    (entry.subtitle === undefined || typeof entry.subtitle === 'string') &&
    (entry.image_url === undefined ||
      entry.image_url === null ||
      typeof entry.image_url === 'string') &&
    (entry.poster_image === undefined ||
      entry.poster_image === null ||
      (typeof entry.poster_image === 'object' &&
        typeof (entry.poster_image as Record<string, unknown>).public_url === 'string')) &&
    (entry.route === undefined || typeof entry.route === 'string') &&
    (entry.query === undefined || typeof entry.query === 'string')
  );
}

function mergeRecentSearches(current: RecentSearchEntry[], entry: RecentSearchEntry) {
  return [entry, ...current.filter((existing) => existing.key !== entry.key)].slice(
    0,
    RECENT_SEARCH_LIMIT
  );
}

async function readRecentSearches() {
  try {
    const value =
      Platform.OS === 'web'
        ? localStorage.getItem(RECENT_SEARCHES_KEY)
        : await SecureStore.getItemAsync(RECENT_SEARCHES_KEY);

    if (!value) {
      return [];
    }

    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(isRecentSearchEntry) : [];
  } catch {
    return [];
  }
}

async function writeRecentSearches(searches: RecentSearchEntry[]) {
  try {
    const value = JSON.stringify(searches);

    if (Platform.OS === 'web') {
      localStorage.setItem(RECENT_SEARCHES_KEY, value);
      return;
    }

    await SecureStore.setItemAsync(RECENT_SEARCHES_KEY, value);
  } catch {
    // best-effort
  }
}

export function useRecentSearches() {
  const [recentSearches, setRecentSearches] = React.useState<RecentSearchEntry[] | null>(null);
  const recentSearchesRef = React.useRef<RecentSearchEntry[]>([]);

  React.useEffect(() => {
    let isMounted = true;

    readRecentSearches().then((value) => {
      if (isMounted) {
        recentSearchesRef.current = value;
        setRecentSearches(value);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const saveSearch = React.useCallback(async (entry: RecentSearchEntry) => {
    const nextSearches = mergeRecentSearches(recentSearchesRef.current, entry);
    recentSearchesRef.current = nextSearches;
    setRecentSearches(nextSearches);
    await writeRecentSearches(nextSearches);
  }, []);

  const clearSearches = React.useCallback(async () => {
    recentSearchesRef.current = [];
    setRecentSearches([]);
    await writeRecentSearches([]);
  }, []);

  const removeSearch = React.useCallback(async (key: string) => {
    const nextSearches = recentSearchesRef.current.filter((entry) => entry.key !== key);
    recentSearchesRef.current = nextSearches;
    setRecentSearches(nextSearches);
    await writeRecentSearches(nextSearches);
  }, []);

  return {
    recentSearches: recentSearches ?? [],
    isHydrated: recentSearches !== null,
    saveSearch,
    clearSearches,
    removeSearch,
  };
}
